import { useState, useCallback } from "react";
import { supabase } from "../../../lib/supabase";

interface Subscriber {
	id: number;
	email: string;
	name: string;
	subscribed_at: string;
	active: boolean;
}

interface NewsletterTabProps {
	subscribers: Subscriber[];
	loading: boolean;
}

export default function NewsletterTab({ subscribers, loading }: NewsletterTabProps) {
	const [message, setMessage] = useState("");

	const activeCount = subscribers.filter((s) => s.active).length;

	const copyAllEmails = useCallback(() => {
		const emails = subscribers
			.filter((s) => s.active)
			.map((s) => s.email)
			.join(", ");
		if (emails) {
			navigator.clipboard.writeText(emails);
			setMessage("Emails copiados al portapapeles.");
			setTimeout(() => setMessage(""), 2500);
		}
	}, [subscribers]);

	const mailtoAllSubscribers = useCallback(() => {
		const emails = subscribers.filter((s) => s.active).map((s) => s.email);
		if (emails.length === 0) return;
		const bcc = emails.join(",");
		window.open(
			`mailto:info@aviacioninsightrd.com?bcc=${encodeURIComponent(bcc)}&subject=${encodeURIComponent("Newsletter Aviación Insight RD")}`,
			"_blank",
		);
	}, [subscribers]);

	const exportSubscribersCSV = useCallback(() => {
		const headers = "Nombre,Email,Estado,Fecha de suscripcion\n";
		const rows = subscribers
			.map((s) => {
				const status = s.active ? "Activo" : "Inactivo";
				const date = s.subscribed_at
					? new Date(s.subscribed_at).toLocaleDateString("es-DO")
					: "";
				return `"${(s.name || "").replace(/"/g, '""')}","${s.email}","${status}","${date}"`;
			})
			.join("\n");
		const csv = "\uFEFF" + headers + rows;
		const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
		const url = URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.download = `suscriptores-aviacion-insight-${new Date().toISOString().split("T")[0]}.csv`;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
		setMessage("CSV descargado correctamente.");
		setTimeout(() => setMessage(""), 2500);
	}, [subscribers]);

	const toggleSubscriber = useCallback(async (id: number, current: boolean) => {
		await supabase
			.from("newsletter_subscribers")
			.update({ active: !current })
			.eq("id", id);
	}, []);

	const deleteSubscriber = useCallback(async (id: number) => {
		await supabase.from("newsletter_subscribers").delete().eq("id", id);
	}, []);

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between">
				<div>
					<h2
						className="text-[#0B1F3A] font-extrabold text-2xl mb-1"
						style={{ fontFamily: "'Sora', sans-serif" }}
					>
						Suscriptores del Newsletter
					</h2>
					<p className="text-slate-500 text-sm">
						Gestiona tus suscriptores y envía comunicaciones.
					</p>
				</div>
			</div>

			{message && (
				<div className="flex items-center gap-2 rounded-xl px-4 py-3 bg-emerald-50 border border-emerald-200 text-emerald-600">
					<i className="ri-check-line" />
					<p className="text-sm font-semibold">{message}</p>
				</div>
			)}

			{/* Acciones masivas */}
			<div className="bg-white rounded-2xl border border-slate-100 p-6">
				<h3
					className="text-[#0B1F3A] font-bold text-sm mb-4"
					style={{ fontFamily: "'Sora', sans-serif" }}
				>
					<i className="ri-send-plane-line mr-1.5 text-emerald-600" />
					Acciones masivas
				</h3>
				<div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
					<button
						onClick={copyAllEmails}
						className="flex items-center gap-3 px-4 py-4 bg-[#0B1F3A] hover:bg-slate-800 text-white rounded-xl transition-colors cursor-pointer group"
					>
						<div className="w-9 h-9 flex items-center justify-center bg-white/10 rounded-lg group-hover:bg-white/20 transition-colors">
							<i className="ri-clipboard-line text-lg" />
						</div>
						<div className="text-left">
							<p
								className="font-bold text-sm"
								style={{ fontFamily: "'Sora', sans-serif" }}
							>
								Copiar emails
							</p>
							<p className="text-white/50 text-xs">
								{activeCount} activos al portapapeles
							</p>
						</div>
					</button>

					<button
						onClick={mailtoAllSubscribers}
						className="flex items-center gap-3 px-4 py-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl transition-colors cursor-pointer group"
					>
						<div className="w-9 h-9 flex items-center justify-center bg-white/10 rounded-lg group-hover:bg-white/20 transition-colors">
							<i className="ri-mail-send-line text-lg" />
						</div>
						<div className="text-left">
							<p
								className="font-bold text-sm"
								style={{ fontFamily: "'Sora', sans-serif" }}
							>
								Enviar newsletter
							</p>
							<p className="text-white/50 text-xs">
								Abre tu correo con todos en BCC
							</p>
						</div>
					</button>

					<button
						onClick={exportSubscribersCSV}
						className="flex items-center gap-3 px-4 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors cursor-pointer group"
					>
						<div className="w-9 h-9 flex items-center justify-center bg-white/10 rounded-lg group-hover:bg-white/20 transition-colors">
							<i className="ri-file-download-line text-lg" />
						</div>
						<div className="text-left">
							<p
								className="font-bold text-sm"
								style={{ fontFamily: "'Sora', sans-serif" }}
							>
								Exportar CSV
							</p>
							<p className="text-white/50 text-xs">
								Descarga la lista completa
							</p>
						</div>
					</button>
				</div>
				<p className="text-slate-400 text-xs mt-3">
					<i className="ri-information-line mr-1" />
					El botón "Enviar newsletter" abre Gmail/Outlook con todos los suscriptores activos en BCC. Desde ahí escribe tu noticia y envía.
				</p>
			</div>

			{/* Stats */}
			<div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
				<div className="bg-white rounded-xl border border-slate-100 p-5 text-center">
					<div className="w-10 h-10 flex items-center justify-center bg-emerald-50 rounded-xl mx-auto mb-3">
						<i className="ri-user-follow-line text-emerald-600 text-xl" />
					</div>
					<p
						className="text-[#0B1F3A] font-extrabold text-3xl mb-1"
						style={{ fontFamily: "'Sora', sans-serif" }}
					>
						{activeCount}
					</p>
					<p className="text-slate-400 text-xs">Suscriptores activos</p>
				</div>
				<div className="bg-white rounded-xl border border-slate-100 p-5 text-center">
					<div className="w-10 h-10 flex items-center justify-center bg-blue-50 rounded-xl mx-auto mb-3">
						<i className="ri-mail-line text-blue-600 text-xl" />
					</div>
					<p
						className="text-[#0B1F3A] font-extrabold text-3xl mb-1"
						style={{ fontFamily: "'Sora', sans-serif" }}
					>
						{subscribers.length}
					</p>
					<p className="text-slate-400 text-xs">Total suscriptores</p>
				</div>
				<div className="bg-white rounded-xl border border-slate-100 p-5 text-center">
					<div className="w-10 h-10 flex items-center justify-center bg-slate-50 rounded-xl mx-auto mb-3">
						<i className="ri-user-unfollow-line text-slate-400 text-xl" />
					</div>
					<p
						className="text-[#0B1F3A] font-extrabold text-3xl mb-1"
						style={{ fontFamily: "'Sora', sans-serif" }}
					>
						{subscribers.filter((s) => !s.active).length}
					</p>
					<p className="text-slate-400 text-xs">Desuscritos</p>
				</div>
			</div>

			{/* Subscribers list */}
			<div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
				<div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
					<h2
						className="text-[#0B1F3A] font-bold text-sm"
						style={{ fontFamily: "'Sora', sans-serif" }}
					>
						Lista de suscriptores ({subscribers.length})
					</h2>
				</div>

				{loading ? (
					<div className="p-6 space-y-3">
						{[1, 2, 3].map((i) => (
							<div
								key={i}
								className="h-14 bg-slate-100 rounded-xl animate-pulse"
							/>
						))}
					</div>
				) : subscribers.length === 0 ? (
					<div className="text-center py-12">
						<div className="w-12 h-12 flex items-center justify-center bg-slate-100 rounded-xl mx-auto mb-3">
							<i className="ri-mail-line text-slate-400 text-2xl" />
						</div>
						<p className="text-slate-400 text-sm">
							Aún no hay suscriptores.
						</p>
						<p className="text-slate-300 text-xs mt-1">
							Cuando alguien se suscriba al newsletter, aparecerá aquí.
						</p>
					</div>
				) : (
					<div className="divide-y divide-slate-50">
						{subscribers.map((sub) => (
							<div
								key={sub.id}
								className="px-6 py-4 flex items-center gap-4 hover:bg-slate-50 transition-colors"
							>
								<div className="w-9 h-9 flex items-center justify-center bg-emerald-50 rounded-full flex-shrink-0">
									<i className="ri-user-line text-emerald-600 text-sm" />
								</div>
								<div className="flex-1 min-w-0">
									<p
										className="text-[#0B1F3A] font-semibold text-sm"
										style={{ fontFamily: "'Sora', sans-serif" }}
									>
										{sub.name || "Sin nombre"}
									</p>
									<p className="text-slate-400 text-xs">{sub.email}</p>
								</div>
								<div className="flex items-center gap-2 flex-shrink-0">
									<span
										className={`text-[9px] font-bold uppercase px-2 py-1 rounded-full cursor-pointer transition-colors ${
											sub.active
												? "bg-emerald-50 text-emerald-600 hover:bg-emerald-100"
												: "bg-slate-100 text-slate-400 hover:bg-slate-200"
										}`}
										onClick={() => toggleSubscriber(sub.id, sub.active)}
									>
										{sub.active ? "Activo" : "Inactivo"}
									</span>
									<p className="text-slate-300 text-[10px] hidden sm:block">
										{sub.subscribed_at
											? new Date(sub.subscribed_at).toLocaleDateString(
													"es-DO",
													{
														day: "2-digit",
														month: "short",
														year: "numeric",
													},
												)
												: "—"}
									</p>
									<a
										href={`mailto:${sub.email}`}
										className="w-8 h-8 flex items-center justify-center rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors cursor-pointer"
										title="Enviar email"
									>
										<i className="ri-mail-send-line text-sm" />
									</a>
									<button
										onClick={() => deleteSubscriber(sub.id)}
										className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors cursor-pointer"
										title="Eliminar suscriptor"
									>
										<i className="ri-delete-bin-line text-sm" />
									</button>
								</div>
							</div>
						))}
					</div>
				)}
			</div>
		</div>
	);
}